/**
 * wc-payment-form.js
 *
 * Handles rendering the GWC payment form in any context:
 *
 *  1. Static embed  — <div class="gwc-payment-form-mount" data-product-id="...">
 *     Boots on DOMContentLoaded. No JS needed in the template.
 *
 *  2. Checkout button — .growtype-wc-checkout-button[data-product-id]
 *     On click: fetches the form HTML via AJAX, injects it into the modal,
 *     then dispatches the selected provider mount event.
 *
 *  3. Dynamic / AJAX — mount points added after page load are caught by
 *     MutationObserver and booted automatically.
 *
 *  4. AJAX context  — if window.growtype_wc_ajax is missing, fetches config
 *     from the REST endpoint before booting.
 */

import {
    debugPaymentForm,
    getContainerDebugSummary,
    isPaymentFormDebugEnabled,
    setPaymentFormDebug,
} from './util/payment-form-debug';
const { requestPaymentFormWithNonceRefresh } = require('./util/payment-form-request');

(function () {
    'use strict';

    // ── Config ────────────────────────────────────────────────────────────────

    var CONFIG_ENDPOINT = '/wp-json/gwc/v1/payment-config';
    var MOUNT_CLASS = 'gwc-payment-form-mount';
    var BOOTED_ATTR = 'data-gwc-booted';
    var BTN_CLASS = 'growtype-wc-checkout-button';
    var CHECKOUT_SELECTOR = '.' + BTN_CLASS + '[data-product-id], .btn-show-paypal-card[data-product-id], a.btn-addtocart[href*="payment_method=gwc-paypal"], a.woocommerce-button.pay[href*="gwc_product_id="]';
    var trackedCommerceMilestones = {};

    function normalizeBoolean(value) {
        return ['yes', '1', 'true'].indexOf(String(value || '').toLowerCase()) !== -1;
    }

    function normalizeProviderFromPaymentMethod(paymentMethod) {
        var provider = String(paymentMethod || '').toLowerCase();

        if (provider.indexOf('gwc-') === 0) {
            provider = provider.slice(4);
        }

        if (provider.indexOf('growtype_wc_') === 0) {
            provider = provider.slice(12);
        }

        return provider.replace(/_/g, '-');
    }

    function getProviderConfig(provider) {
        var config = window.growtype_wc_ajax || {};
        return config && config[provider] ? config[provider] : {};
    }

    function getDefaultPaymentFormAction(provider) {
        var actions = {
            stripe: 'gwc_stripe_payment_form',
            paypal: 'gwc_payment_form',
        };

        return actions[provider] || '';
    }

    function getPaymentFormNonce(provider, explicitNonce) {
        if (explicitNonce) {
            return explicitNonce;
        }

        var providerConfig = getProviderConfig(provider);
        if (providerConfig.payment_form_nonce) {
            return providerConfig.payment_form_nonce;
        }

        if (providerConfig.nonce) {
            return providerConfig.nonce;
        }

        return window.growtype_wc_ajax && window.growtype_wc_ajax.nonce
            ? window.growtype_wc_ajax.nonce
            : '';
    }

    function normalizeCheckoutAttemptId(value) {
        var attemptId = String(value || '').toLowerCase();
        return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(attemptId)
            ? attemptId
            : '';
    }

    function checkoutAttemptIdForButton(button) {
        if (!button) return '';

        return normalizeCheckoutAttemptId(
            button.getAttribute('data-checkout-attempt-id')
            || getUrlParam(button.getAttribute('data-fallback') || button.getAttribute('href'), 'gwc_checkout_attempt')
        );
    }

    function trackCommerceMilestone(eventName, paymentMethod, checkoutAttemptId, extraData) {
        if (typeof window.growtypeAnalyticsCapture !== 'function') return false;

        var normalizedPaymentMethod = String(paymentMethod || '').toLowerCase();
        var normalizedCheckoutAttemptId = normalizeCheckoutAttemptId(checkoutAttemptId);
        var milestoneKey = eventName + '|' + normalizedPaymentMethod + '|' + normalizedCheckoutAttemptId;
        if (trackedCommerceMilestones[milestoneKey]) return true;

        var config = window.growtype_wc_ajax || {};
        var data = Object.assign({}, extraData || {});
        if (
            (typeof config.cart_total === 'number' || typeof config.cart_total === 'string')
            && String(config.cart_total).length <= 32
        ) {
            data.value = config.cart_total;
        }
        if (typeof config.currency === 'string' && /^[A-Z]{3}$/i.test(config.currency)) {
            data.currency = config.currency.toUpperCase();
        }
        if (/^[a-z0-9_-]{1,64}$/.test(normalizedPaymentMethod)) {
            data.payment_method = normalizedPaymentMethod;
        }
        if (normalizedCheckoutAttemptId) {
            data.checkout_attempt_id = normalizedCheckoutAttemptId;
        }

        trackedCommerceMilestones[milestoneKey] = true;
        window.growtypeAnalyticsCapture(eventName, data);
        return true;
    }

    function checkoutEvent(stage, paymentMethod, checkoutAttemptId, extraData) {
        return trackCommerceMilestone(
            'growtype_analytics_growtype_wc_' + stage,
            paymentMethod,
            checkoutAttemptId,
            extraData
        );
    }

    function appendCheckoutAttempt(url, checkoutAttemptId, stage) {
        var attemptId = normalizeCheckoutAttemptId(checkoutAttemptId);
        if (!url || !attemptId) return url || '';
        try {
            var parsed = new URL(url, window.location.href);
            parsed.searchParams.set('gwc_checkout_attempt', attemptId);
            if (stage) parsed.searchParams.set('gwc_checkout_stage', stage);
            return parsed.toString();
        } catch (error) {
            return url;
        }
    }

    function trackCheckoutOfferImpressions(root) {
        var scope = root || document;
        var buttons = Array.from(scope.querySelectorAll ? scope.querySelectorAll(CHECKOUT_SELECTOR) : []);
        if (scope.matches && scope.matches(CHECKOUT_SELECTOR)) buttons.unshift(scope);
        buttons.forEach(function (button) {
            if (button.getAttribute('data-checkout-offer-tracked') === '1') return;
            var sent = checkoutEvent(
                'offer_impression',
                button.getAttribute('data-primary-payment-method') || '',
                checkoutAttemptIdForButton(button),
                { product_id: String(button.getAttribute('data-product-id') || '').slice(0, 32) }
            );
            if (sent !== false) button.setAttribute('data-checkout-offer-tracked', '1');
        });
    }

    function getUrlParam(url, key) {
        try {
            return new URL(url, window.location.href).searchParams.get(key) || '';
        } catch (err) {
            return '';
        }
    }

    function isPaypalAddToCartLink(el) {
        if (!el || !el.matches || !el.matches('a.btn-addtocart[href]')) {
            return false;
        }

        return getUrlParam(el.getAttribute('href'), 'payment_method') === 'gwc-paypal';
    }

    function selectedProductIdForButton(button) {
        var wrapper = button.closest('.b-products');
        if (!wrapper) return button.getAttribute('data-product-id') || '';

        var checked = wrapper.querySelector('.growtype-credit-product-input:checked');
        if (checked && checked.value) return checked.value;

        var selected = wrapper.querySelector('.products .product.is-selected');
        if (!selected) return '';

        var productClass = Array.prototype.find.call(selected.classList, function (name) {
            return name.indexOf('post-') === 0;
        });
        return productClass ? productClass.substring(5) : '';
    }

    function checkoutUrlForProduct(url, productId) {
        if (!url || !productId) return url || '';

        try {
            var parsed = new URL(url, window.location.href);
            var hasProductParam = false;
            ['add-to-cart', 'product_id', 'gwc_product_id'].forEach(function (key) {
                if (parsed.searchParams.has(key)) {
                    parsed.searchParams.set(key, productId);
                    hasProductParam = true;
                }
            });
            if (!hasProductParam) parsed.searchParams.set('add-to-cart', productId);
            return parsed.toString();
        } catch (error) {
            var updatedUrl = url
                .replace(/add-to-cart=\d+/, 'add-to-cart=' + productId)
                .replace(/product_id=\d+/, 'product_id=' + productId)
                .replace(/gwc_product_id=\d+/, 'gwc_product_id=' + productId);
            if (updatedUrl === url) {
                updatedUrl += (url.indexOf('?') === -1 ? '?' : '&') + 'add-to-cart=' + encodeURIComponent(productId);
            }
            return updatedUrl;
        }
    }

    function purchaseIntentIdempotencyKey(button, productId) {
        var existing = button.getAttribute('data-purchase-intent-key');
        var existingProduct = button.getAttribute('data-purchase-intent-key-product');
        if (existing && existingProduct === String(productId)) return existing;

        var randomPart = (window.crypto && typeof window.crypto.randomUUID === 'function')
            ? window.crypto.randomUUID()
            : String(Date.now()) + '-' + Math.random().toString(36).slice(2);
        var key = 'purchase-' + productId + '-' + randomPart;
        button.setAttribute('data-purchase-intent-key', key);
        button.setAttribute('data-purchase-intent-key-product', String(productId));
        return key;
    }

    function ensurePurchaseIntentStyles() {
        if (document.getElementById('gwc-purchase-intent-styles')) return;

        var style = document.createElement('style');
        style.id = 'gwc-purchase-intent-styles';
        style.textContent = `
            #gwc-purchase-intent-confirmation .modal-dialog {
                max-width: 460px;
            }
            .gwc-purchase-intent__panel {
                overflow: hidden;
                border: 1px solid rgba(255, 255, 255, .1);
                border-radius: 24px;
                background:
                    radial-gradient(circle at 90% 0%, rgba(255, 144, 0, .13), transparent 34%),
                    linear-gradient(155deg, #202023 0%, #141416 70%);
                color: #fff;
                box-shadow: 0 30px 90px rgba(0, 0, 0, .58), 0 1px 0 rgba(255, 255, 255, .06) inset;
            }
            #gwc-purchase-intent-confirmation .modal-body {
                padding: 28px;
            }
            .gwc-purchase-intent__header {
                display: grid;
                grid-template-columns: 46px 1fr;
                gap: 14px;
                align-items: center;
                margin-bottom: 24px;
            }
            .gwc-purchase-intent__icon {
                display: grid;
                place-items: center;
                width: 46px;
                height: 46px;
                padding: 4px;
                border-radius: 15px;
                background: rgba(255, 144, 0, .12);
                color: var(--gc-main-color, #ff9000);
                box-shadow: 0 10px 28px rgba(255, 144, 0, .1);
            }
            .gwc-purchase-intent__icon svg {
                width: 22px;
                height: 22px;
            }
            .gwc-purchase-intent__icon--product {
                overflow: hidden;
                border-color: rgba(255, 255, 255, .14);
                background: rgba(255, 255, 255, .06);
            }
            .gwc-purchase-intent__icon--product img {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }
            .gwc-purchase-intent__eyebrow {
                margin: 0 0 4px;
                color: rgba(255, 255, 255, .5);
                font-size: 11px;
                font-weight: 750;
                line-height: 1.2;
                letter-spacing: .14em;
                text-transform: uppercase;
            }
            .gwc-purchase-intent__title {
                margin: 0;
                color: #fff;
                font-size: clamp(22px, 4vw, 26px);
                font-weight: 750;
                line-height: 1.15;
                letter-spacing: -.025em;
            }
            .gwc-purchase-intent__summary {
                display: flex;
                gap: 18px;
                align-items: flex-end;
                justify-content: space-between;
                padding: 0 0 22px;
                border-bottom: 1px solid rgba(255, 255, 255, .1);
            }
            .gwc-purchase-intent__product-label {
                display: block;
                margin-bottom: 5px;
                color: rgba(255, 255, 255, .52);
                font-size: 12px;
                font-weight: 650;
                letter-spacing: .04em;
            }
            .gwc-purchase-intent__product {
                display: block;
                color: rgba(255, 255, 255, .94);
                font-size: 17px;
                font-weight: 650;
                line-height: 1.3;
            }
            .gwc-purchase-intent__price {
                flex: 0 0 auto;
                color: #fff;
                font-size: 24px;
                font-weight: 780;
                line-height: 1;
                letter-spacing: -.03em;
                white-space: nowrap;
            }
            .gwc-purchase-intent__terms {
                display: grid;
                grid-template-columns: 18px 1fr;
                gap: 10px;
                align-items: start;
                margin: 18px 0 24px;
                color: rgba(255, 255, 255, .64);
                font-size: 14px;
                line-height: 1.55;
            }
            .gwc-purchase-intent__terms svg {
                width: 17px;
                height: 17px;
                margin-top: 2px;
                color: var(--gc-main-color, #ff9000);
            }
            .gwc-purchase-intent__actions {
                display: grid;
                grid-template-columns: minmax(100px, .7fr) minmax(180px, 1.3fr);
                gap: 10px;
            }
            .gwc-purchase-intent__button {
                min-width: initial;
            }
            .gwc-purchase-intent__button:hover {
                transform: translateY(-1px);
            }
            .gwc-purchase-intent__button:active {
                transform: translateY(0) scale(.985);
            }
            .gwc-purchase-intent__button:focus-visible {
                outline: 3px solid rgba(255, 144, 0, .32);
                outline-offset: 3px;
            }
            .gwc-purchase-intent__button--cancel {
                border-color: rgba(255, 255, 255, .12);
                background: rgba(255, 255, 255, .055);
                color: rgba(255, 255, 255, .82);
            }
            .gwc-purchase-intent__button--cancel:hover {
                border-color: rgba(255, 255, 255, .2);
                background: rgba(255, 255, 255, .09);
                color: #fff;
            }
            @media (max-width: 520px) {
                #gwc-purchase-intent-confirmation .modal-dialog {
                    margin: 12px;
                }
                #gwc-purchase-intent-confirmation .modal-body {
                    padding: 24px 20px 20px;
                }
                .gwc-purchase-intent__actions {
                    grid-template-columns: 1fr;
                }
                .gwc-purchase-intent__button--confirm { order: 1; }
                .gwc-purchase-intent__button--cancel { order: 2; }
            }
            @media (prefers-reduced-motion: reduce) {
                #gwc-purchase-intent-confirmation,
                #gwc-purchase-intent-confirmation * {
                    animation-duration: .01ms !important;
                    transition-duration: .01ms !important;
                }
            }
        `;
        document.head.appendChild(style);
    }

    function showPurchaseIntentConfirmation(details, paymentMethod, checkoutAttemptId) {
        return new Promise(function (resolve) {
            var old = document.getElementById('gwc-purchase-intent-confirmation');
            if (old) old.remove();

            ensurePurchaseIntentStyles();
            var previouslyFocused = document.activeElement;

            var modal = document.createElement('div');
            modal.id = 'gwc-purchase-intent-confirmation';
            modal.className = 'modal fade gwc-purchase-intent-modal';
            modal.tabIndex = -1;
            modal.setAttribute('aria-hidden', 'true');
            modal.setAttribute('aria-labelledby', 'gwc-purchase-intent-title');
            modal.setAttribute('aria-describedby', 'gwc-purchase-intent-description');

            var dialog = document.createElement('div');
            dialog.className = 'modal-dialog modal-dialog-centered';

            var panel = document.createElement('div');
            panel.className = 'modal-content gwc-purchase-intent__panel';

            var body = document.createElement('div');
            body.className = 'modal-body';

            var header = document.createElement('div');
            header.className = 'gwc-purchase-intent__header';
            var icon = document.createElement('span');
            icon.className = 'gwc-purchase-intent__icon';
            icon.setAttribute('aria-hidden', 'true');
            var fallbackIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.8 14.5 9l6.2 2.5-6.2 2.5-2.5 6.2L9.5 14l-6.2-2.5L9.5 9 12 2.8Z"/></svg>';
            if (details.product_image_url) {
                var productImage = document.createElement('img');
                productImage.src = details.product_image_url;
                productImage.alt = '';
                productImage.addEventListener('error', function () {
                    icon.classList.remove('gwc-purchase-intent__icon--product');
                    icon.innerHTML = fallbackIcon;
                }, { once: true });
                icon.classList.add('gwc-purchase-intent__icon--product');
                icon.appendChild(productImage);
            } else {
                icon.innerHTML = fallbackIcon;
            }
            var heading = document.createElement('div');
            var eyebrow = document.createElement('p');
            eyebrow.className = 'gwc-purchase-intent__eyebrow';
            eyebrow.textContent = 'One-time purchase';
            var title = document.createElement('h2');
            title.id = 'gwc-purchase-intent-title';
            title.className = 'gwc-purchase-intent__title';
            title.textContent = 'Confirm your purchase';
            heading.appendChild(eyebrow);
            heading.appendChild(title);
            header.appendChild(icon);
            header.appendChild(heading);

            var summary = document.createElement('div');
            summary.className = 'gwc-purchase-intent__summary';
            var productWrap = document.createElement('div');
            var productLabel = document.createElement('span');
            productLabel.className = 'gwc-purchase-intent__product-label';
            productLabel.textContent = 'You will receive';
            var product = document.createElement('span');
            product.className = 'gwc-purchase-intent__product';
            product.textContent = details.product_name;
            var price = document.createElement('strong');
            price.className = 'gwc-purchase-intent__price';
            price.textContent = details.price + ' ' + details.currency;
            productWrap.appendChild(productLabel);
            productWrap.appendChild(product);
            summary.appendChild(productWrap);
            summary.appendChild(price);

            var terms = document.createElement('div');
            terms.id = 'gwc-purchase-intent-description';
            terms.className = 'gwc-purchase-intent__terms';
            terms.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="11" rx="3"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg><span>One-time payment. Your saved payment method will be charged once.</span>';
            var actions = document.createElement('div');
            actions.className = 'gwc-purchase-intent__actions';
            var cancel = document.createElement('button');
            cancel.type = 'button';
            cancel.className = 'btn btn-secondary gwc-purchase-intent__button gwc-purchase-intent__button--cancel';
            cancel.textContent = 'Cancel';
            var confirm = document.createElement('button');
            confirm.type = 'button';
            confirm.className = 'btn btn-primary gwc-purchase-intent__button gwc-purchase-intent__button--confirm';
            confirm.textContent = 'Confirm ' + details.price + ' ' + details.currency;

            actions.appendChild(cancel);
            actions.appendChild(confirm);
            body.appendChild(header);
            body.appendChild(summary);
            body.appendChild(terms);
            body.appendChild(actions);
            panel.appendChild(body);
            dialog.appendChild(panel);
            modal.appendChild(dialog);
            document.body.appendChild(modal);
            checkoutEvent('confirmation_shown', paymentMethod, checkoutAttemptId);

            var settled = false;
            var confirmed = false;
            function cleanup() {
                if (settled) return;
                settled = true;
                modal.remove();
                if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                    previouslyFocused.focus();
                }
                resolve(confirmed);
            }
            function close(value) {
                confirmed = value;
                if (value) {
                    checkoutEvent('confirmation_confirmed', paymentMethod, checkoutAttemptId);
                } else {
                    checkoutEvent('confirmation_cancelled', paymentMethod, checkoutAttemptId);
                }
                window.growtypeModal.hide(modal);
            }
            cancel.addEventListener('click', function () { close(false); }, { once: true });
            confirm.addEventListener('click', function () { close(true); }, { once: true });
            modal.addEventListener('shown.bs.modal', function () { confirm.focus(); }, { once: true });
            modal.addEventListener('hidden.bs.modal', cleanup, { once: true });

            if (!window.growtypeModal || typeof window.growtypeModal.show !== 'function') {
                checkoutEvent('confirmation_cancelled', paymentMethod, checkoutAttemptId, { reason: 'modal_unavailable' });
                cleanup();
                return;
            }
            window.growtypeModal.show(modal);
        });
    }

    async function refreshPaymentSecurity(ajaxUrl) {
        var response = await fetch(ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            credentials: 'same-origin',
            cache: 'no-store',
            body: new URLSearchParams({
                action: 'growtype_wc_refresh_payment_nonce',
            }).toString(),
        });
        var result = await response.json();
        var nonce = result && result.success && result.data ? result.data.nonce : '';
        var paymentFormNonce = result && result.success && result.data ? result.data.payment_form_nonce : '';
        var refreshedUserId = result && result.success && result.data ? Number(result.data.user_id || 0) : 0;
        var renderedUserId = Number(window.growtype_wc_ajax && window.growtype_wc_ajax.user_id || 0);

        if (!nonce) {
            throw new Error('Could not refresh purchase security. Please reload and try again.');
        }

        if (renderedUserId > 0 && refreshedUserId !== renderedUserId) {
            throw new Error('Your login session changed. Please sign out and sign in again, then retry.');
        }

        window.growtype_wc_ajax = window.growtype_wc_ajax || {};
        window.growtype_wc_ajax.nonce = nonce;
        window.growtype_wc_ajax.user_id = refreshedUserId;
        window.growtype_wc_ajax.paypal = window.growtype_wc_ajax.paypal || {};
        if (paymentFormNonce) {
            window.growtype_wc_ajax.paypal.payment_form_nonce = paymentFormNonce;
        }

        return {
            nonce: nonce,
            paymentFormNonce: paymentFormNonce,
        };
    }

    async function refreshPaymentNonce(ajaxUrl) {
        return (await refreshPaymentSecurity(ajaxUrl)).nonce;
    }

    async function refreshPaymentFormNonce(ajaxUrl, provider) {
        var refreshed = await refreshPaymentSecurity(ajaxUrl);
        var nonce = provider === 'paypal' ? refreshed.paymentFormNonce : refreshed.nonce;

        if (!nonce) {
            throw new Error('Could not refresh checkout security. Please reload and try again.');
        }

        return nonce;
    }

    async function requestPurchaseIntent(ajaxUrl, body) {
        var response = await fetch(ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            credentials: 'same-origin',
            body: body.toString(),
        });

        // WordPress returns HTTP 403 with body "-1" when a cached nonce is no
        // longer valid. Refresh it from an uncached endpoint and retry once.
        if (response.status === 403) {
            body.set('nonce', await refreshPaymentNonce(ajaxUrl));
            response = await fetch(ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                credentials: 'same-origin',
                body: body.toString(),
            });
        }

        return response.json();
    }

    async function prepareAndConfirmPurchaseIntent(button) {
        var productId = selectedProductIdForButton(button);
        if (!productId) {
            window.alert('Please select a credit pack before continuing.');
            return;
        }

        var config = window.growtype_wc_ajax || {};
        var ajaxUrl = config.url || '/wp-admin/admin-ajax.php';
        var action = button.getAttribute('data-purchase-intent-action') || 'growtype_wc_create_purchase_intent';
        var providerId = button.getAttribute('data-provider') || normalizeProviderFromPaymentMethod(button.getAttribute('data-primary-payment-method') || '');
        var paymentMethod = button.getAttribute('data-primary-payment-method') || providerId;
        var checkoutAttemptId = checkoutAttemptIdForButton(button);
        trackCommerceMilestone('growtype_analytics_growtype_wc_begin_checkout', paymentMethod, checkoutAttemptId);
        var body = new URLSearchParams({
            action: action,
            nonce: config.nonce || '',
            product_id: productId,
            provider_id: providerId,
            return_url: button.getAttribute('data-return-url') || '',
            idempotency_key: purchaseIntentIdempotencyKey(button, productId),
            checkout_attempt_id: checkoutAttemptId,
        });

        button.classList.add('processing');
        button.setAttribute('aria-disabled', 'true');

        try {
            var result = await requestPurchaseIntent(ajaxUrl, body);
            if (!result || !result.success) {
                throw new Error(result && result.data && result.data.message ? result.data.message : 'Could not validate this purchase.');
            }
            if (result.data.status === 'completed' && result.data.redirect_url) {
                checkoutEvent('provider_return', paymentMethod, checkoutAttemptId, { outcome: 'already_completed' });
                window.location.href = appendCheckoutAttempt(result.data.redirect_url, checkoutAttemptId, 'provider_return');
                return;
            }

            var confirmed = await showPurchaseIntentConfirmation(result.data, paymentMethod, checkoutAttemptId);
            if (confirmed && result.data.charge_url) {
                trackCommerceMilestone('growtype_analytics_growtype_wc_begin_payment', paymentMethod, checkoutAttemptId);
                checkoutEvent('provider_redirect', paymentMethod, checkoutAttemptId);
                window.location.href = appendCheckoutAttempt(result.data.charge_url, checkoutAttemptId, 'provider_redirect');
                return;
            }
        } catch (error) {
            checkoutEvent('payment_form_error', paymentMethod, checkoutAttemptId, { error_code: 'purchase_intent_error' });
            window.alert(error && error.message ? error.message : 'Could not validate this purchase.');
        }

        button.classList.remove('processing');
        button.removeAttribute('aria-disabled');
    }

    // Protected products never expose or follow a directly chargeable URL.
    // Capture the click before legacy delegated handlers can act on it.
    document.addEventListener('click', function (event) {
        var target = event.target && event.target.nodeType === 3 ? event.target.parentNode : event.target;
        var button = target && target.closest ? target.closest('[data-purchase-intent-required="1"]') : null;
        if (!button) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (button.classList.contains('processing')) return;
        prepareAndConfirmPurchaseIntent(button);
    }, true);

    // ── Config resolution ─────────────────────────────────────────────────────

    var _configPromise = null;

    /**
     * Show a loader spinner inside a container.
     */
    function showSpinner(container) {
        debugPaymentForm('show_spinner:start', {
            container: getContainerDebugSummary(container),
        });

        if (!container) return null;
        var loader = container.querySelector('.gwc-payment-form-mainloader');
        if (loader) {
            debugPaymentForm('show_spinner:reuse_existing_loader', {
                loader: getContainerDebugSummary(loader),
            });
            return loader;
        }

        loader = document.createElement('div');
        loader.className = 'gwc-payment-form-mainloader';
        loader.innerHTML = '<div class="gwc-hf-spinner"></div>';
        container.appendChild(loader);
        debugPaymentForm('show_spinner:created', {
            loader: getContainerDebugSummary(loader),
            container: getContainerDebugSummary(container),
        });
        return loader;
    }

    /**
     * Hide/remove a loader spinner inside a container or the loader element itself.
     */
    function hideSpinner(loaderOrContainer) {
        debugPaymentForm('hide_spinner:start', {
            target: getContainerDebugSummary(loaderOrContainer),
        });

        if (!loaderOrContainer) return;
        var loader = (loaderOrContainer.classList && loaderOrContainer.classList.contains('gwc-payment-form-mainloader'))
            ? loaderOrContainer
            : loaderOrContainer.querySelector('.gwc-payment-form-mainloader');

        if (loader) {
            loader.remove();
            debugPaymentForm('hide_spinner:removed');
        } else {
            debugPaymentForm('hide_spinner:not_found');
        }
    }

    /**
     * Remove the processing state from all payment/add-to-cart buttons.
     */
    function resetProcessingButtons() {
        debugPaymentForm('reset_processing_buttons:start');

        var processingBtns = document.querySelectorAll('.processing');
        Array.prototype.forEach.call(processingBtns, function (btn) {
            btn.classList.remove('processing');
        });
        if (window.jQuery) {
            window.jQuery('.processing').removeClass('processing');
        }
    }

    function renderFallbackCta(detail) {
        if (!detail || !detail.container || !detail.fallback) {
            debugPaymentForm('fallback_cta:blocked_missing_detail_or_fallback', {
                detail: detail,
            });
            return;
        }

        var container = document.querySelector(detail.container);

        if (!container) {
            debugPaymentForm('fallback_cta:blocked_missing_container', {
                detail: detail,
            });
            return;
        }

        var wrapper = container.closest('.gwc-payment-form') || container;
        wrapper.innerHTML = '';

        var link = document.createElement('a');
        link.href = detail.fallback;
        link.className = 'btn btn-primary w-100 gwc-payment-form-fallback-cta';
        link.style.cssText = 'min-height:48px;display:flex;align-items:center;justify-content:center;font-weight:700;';
        link.textContent = detail.label || 'Continue to secure checkout';

        var note = document.createElement('div');
        note.className = 'gwc-payment-form-fallback-note';
        note.style.cssText = 'margin-top:12px;text-align:center;font-size:13px;color:#667085;';
        note.textContent = 'Wallet checkout is unavailable in this browser. Continue with secure checkout instead.';

        wrapper.appendChild(link);
        wrapper.appendChild(note);
        debugPaymentForm('fallback_cta:rendered', {
            detail: detail,
            wrapper: getContainerDebugSummary(wrapper),
        });
    }

    document.addEventListener('growtype_wc_payment_express_ready', function (e) {
        var selector = e.detail && e.detail.container ? e.detail.container : '';
        debugPaymentForm('payment_event:express_ready', {
            detail: e.detail,
            container: selector ? getContainerDebugSummary(document.querySelector(selector)) : null,
        });
    });

    document.addEventListener('growtype_wc_payment_fallback', function (e) {
        var selector = e.detail && e.detail.container ? e.detail.container : '';
        debugPaymentForm('payment_event:fallback', {
            detail: e.detail,
            container: selector ? getContainerDebugSummary(document.querySelector(selector)) : null,
        });
        renderFallbackCta(e.detail);
    });

    document.addEventListener('growtype_wc_payment_reset', function (e) {
        debugPaymentForm('payment_event:reset', {
            detail: e.detail,
        });
    });

    /**
     * Bind close triggers and hidden state resets to the modal.
     */
    function bindModalEvents(modal) {
        if (modal.getAttribute('data-gwc-bound')) {
            debugPaymentForm('bind_modal_events:already_bound', {
                modal: getContainerDebugSummary(modal),
            });
            return;
        }
        debugPaymentForm('bind_modal_events:binding', {
            modal: getContainerDebugSummary(modal),
        });
        modal.setAttribute('data-gwc-bound', '1');

        // Bind close button
        var closeBtn = modal.querySelector('.btn-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', function () {
                if (window.bootstrap && window.bootstrap.Modal) {
                    var inst = window.bootstrap.Modal.getInstance(modal);
                    if (inst) inst.hide();
                } else if (window.jQuery && window.jQuery.fn.modal) {
                    window.jQuery(modal).modal('hide');
                } else {
                    modal.classList.remove('show');
                    modal.style.display = 'none';
                    var backdrop = document.querySelector('.modal-backdrop');
                    if (backdrop) backdrop.remove();
                }
                resetProcessingButtons();
            });
        }

        // Reset content when hidden
        modal.addEventListener('hidden.bs.modal', function () {
            debugPaymentForm('modal:hidden_reset_content', {
                modal: getContainerDebugSummary(modal),
            });

            var modalBody = modal.querySelector('.modal-body-payment-form');
            if (modalBody) {
                modalBody.innerHTML = '';
            }
            resetProcessingButtons();
        });
    }

    /**
     * Get the server-rendered payment form modal, or fetch its HTML from
     * Growtype_Wc_Payment_Form_Modal::ajax_render() when not in the DOM.
     * Returns a Promise that resolves with the modal element.
     */
    function getOrCreatePaymentFormModal() {
        var modalId = 'gwcPaymentFormModal';
        var modal = document.getElementById(modalId);
        if (modal) {
            debugPaymentForm('modal:existing_found', {
                modal: getContainerDebugSummary(modal),
            });
            bindModalEvents(modal);
            return Promise.resolve(modal);
        }

        debugPaymentForm('modal:not_found_fetching_shell');

        // Modal was not server-rendered — fetch the shell HTML from PHP.
        return resolveConfig().then(function () {
            var ajaxUrl = (window.growtype_wc_ajax && window.growtype_wc_ajax.url)
                ? window.growtype_wc_ajax.url
                : '/wp-admin/admin-ajax.php';

            debugPaymentForm('modal:shell_fetch:start', {
                ajaxUrl: ajaxUrl,
            });

            return fetch(ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ action: 'gwc_payment_form_modal' }).toString(),
            });
        })
        .then(function (r) { return r.json(); })
        .then(function (res) {
            debugPaymentForm('modal:shell_fetch:response', {
                success: !!(res && res.success),
                dataKeys: res && res.data ? Object.keys(res.data) : [],
            });

            if (!res.success) {
                throw new Error('[wc-payment-form] gwc_payment_form_modal AJAX error');
            }

            var tmp = document.createElement('div');
            tmp.innerHTML = res.data.html;
            modal = tmp.firstElementChild;

            document.body.appendChild(modal);
            bindModalEvents(modal);
            debugPaymentForm('modal:shell_appended', {
                modal: getContainerDebugSummary(modal),
            });

            return modal;
        });
    }

    /**
     * Show the Bootstrap modal.
     */
    function showPaymentFormModal(modal) {
        debugPaymentForm('modal:show:start', {
            modal: getContainerDebugSummary(modal),
            hasBootstrap: !!(window.bootstrap && window.bootstrap.Modal),
            hasJqueryModal: !!(window.jQuery && window.jQuery.fn.modal),
        });

        if (window.bootstrap && window.bootstrap.Modal) {
            var bsModal = window.bootstrap.Modal.getInstance(modal) || new window.bootstrap.Modal(modal);
            bsModal.show();
            debugPaymentForm('modal:show:bootstrap');
        } else if (window.jQuery && window.jQuery.fn.modal) {
            window.jQuery(modal).modal('show');
            debugPaymentForm('modal:show:jquery');
        } else {
            modal.style.display = 'block';
            modal.classList.add('show');
            var backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            document.body.appendChild(backdrop);
            debugPaymentForm('modal:show:fallback_dom');
        }
    }

    /**
     * Returns a Promise that resolves with window.growtype_wc_ajax.
     * Fetches from REST if not already present.
     */
    function resolveConfig() {
        if (window.growtype_wc_ajax) {
            debugPaymentForm('config:existing_window_config', {
                hasUrl: !!window.growtype_wc_ajax.url,
                hasNonce: !!window.growtype_wc_ajax.nonce,
                hasStripe: !!window.growtype_wc_ajax.stripe,
                hasPaypal: !!window.growtype_wc_ajax.paypal,
            });
            return Promise.resolve(window.growtype_wc_ajax);
        }
        if (_configPromise) {
            debugPaymentForm('config:reuse_pending_request');
            return _configPromise;
        }
        debugPaymentForm('config:fetch:start', {
            endpoint: CONFIG_ENDPOINT,
        });
        _configPromise = fetch(CONFIG_ENDPOINT)
            .then(function (r) { return r.json(); })
            .then(function (data) {
                window.growtype_wc_ajax = data;
                debugPaymentForm('config:fetch:success', {
                    hasUrl: !!data.url,
                    hasNonce: !!data.nonce,
                    hasStripe: !!data.stripe,
                    hasPaypal: !!data.paypal,
                });
                return data;
            })
            .catch(function (err) {
                debugPaymentForm('config:fetch:error', err);
                console.error('[wc-payment-form] Failed to fetch config:', err);
                return {};
            });
        return _configPromise;
    }

    // ── Mount point initializer ───────────────────────────────────────────────

    /**
     * Boot all unbooted .gwc-payment-form-mount elements inside `root`.
     */
    function initMounts(root) {
        var selector = '.' + MOUNT_CLASS + ':not([' + BOOTED_ATTR + '])';
        var mounts = root.querySelectorAll ? root.querySelectorAll(selector) : [];
        debugPaymentForm('init_mounts:start', {
            selector: selector,
            root: getContainerDebugSummary(root === document ? document.documentElement : root),
            found: mounts.length,
        });

        Array.prototype.forEach.call(mounts, function (el) {
            el.setAttribute(BOOTED_ATTR, '1');

            var productId = el.getAttribute('data-product-id') || '0';
            var methods = el.getAttribute('data-methods') || 'applepay,googlepay,paypal';
            var returnUrl = el.getAttribute('data-return-url') || '';
            var provider = (el.getAttribute('data-provider') || 'paypal').toLowerCase();
            var containerId = MOUNT_CLASS + '-' + productId + '-' + Math.random().toString(36).slice(2, 7);
            debugPaymentForm('init_mounts:boot_mount', {
                productId: productId,
                methods: methods,
                returnUrl: returnUrl,
                provider: provider,
                originalElement: getContainerDebugSummary(el),
                containerId: containerId,
            });

            // Promote the placeholder to an express container
            el.id = containerId;
            el.className = 'gwc-payment-form__express';
            el.setAttribute('data-method', methods);
            el.style.minHeight = '48px';
            debugPaymentForm('init_mounts:promoted_placeholder', {
                element: getContainerDebugSummary(el),
            });

            resolveConfig().then(function () {
                debugPaymentForm('init_mounts:dispatch_mount_express', {
                    provider: provider,
                    container: '#' + containerId,
                    productId: parseInt(productId, 10),
                    methods: methods,
                    returnUrl: returnUrl,
                });

                document.dispatchEvent(new CustomEvent('growtype_wc_payment_request', {
                    detail: {
                        provider: provider,
                        type: 'mount_express',
                        container: '#' + containerId,
                        productId: parseInt(productId, 10),
                        method: methods,
                        returnUrl: returnUrl,
                    }
                }));
            });
        });
    }

    // ── Checkout button → payment form transition ─────────────────────────────

    document.addEventListener('click', function (e) {
        var btn = e.target.closest(CHECKOUT_SELECTOR);
        if (!btn) return;

        if (
            btn.classList.contains('processing')
            || btn.getAttribute('aria-disabled') === 'true'
            || btn.disabled === true
        ) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }

        var isPaypalAddToCart = isPaypalAddToCartLink(btn);
        var isOrderPay = btn.matches('a.woocommerce-button.pay[href*="gwc_product_id="]');
        var initialPaymentMethod = (
            btn.getAttribute('data-primary-payment-method')
            || ((isPaypalAddToCart || isOrderPay) ? 'gwc-paypal' : '')
        ).toLowerCase();
        var checkoutAttemptId = checkoutAttemptIdForButton(btn);

        checkoutEvent('checkout_cta_click', initialPaymentMethod, checkoutAttemptId);
        trackCommerceMilestone('growtype_analytics_growtype_wc_begin_checkout', initialPaymentMethod, checkoutAttemptId);

        if (isPaypalAddToCart || isOrderPay) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }

        debugPaymentForm('checkout_click:matched_button', {
            button: getContainerDebugSummary(btn),
            productId: btn.getAttribute('data-product-id'),
            primaryPaymentMethod: btn.getAttribute('data-primary-payment-method'),
            methods: btn.getAttribute('data-methods'),
            isPaypalAddToCart: isPaypalAddToCart,
            isOrderPay: isOrderPay,
            hasInstantChargeUrl: !!btn.getAttribute('data-instant-charge-url'),
            instantCharge: btn.getAttribute('data-instant-charge'),
        });

        var paymentFormMode = (btn.getAttribute('data-payment-form-mode') || ((isPaypalAddToCart || isOrderPay) ? 'form' : '')).toLowerCase();
        var expressShowForm = (btn.getAttribute('data-express-show-form') || btn.getAttribute('data-stripe-express-show-form') || 'no').toLowerCase();
        var shouldShowPaymentForm = paymentFormMode
            ? paymentFormMode === 'form'
            : normalizeBoolean(expressShowForm);
        var instantUrl = btn.getAttribute('data-instant-charge-url');
        if (instantUrl && !shouldShowPaymentForm) {
            trackCommerceMilestone('growtype_analytics_growtype_wc_begin_payment', initialPaymentMethod, checkoutAttemptId);
            checkoutEvent('provider_redirect', initialPaymentMethod, checkoutAttemptId);
            debugPaymentForm('checkout_click:instant_charge_redirect', {
                instantUrl: instantUrl,
                paymentFormMode: paymentFormMode,
                shouldShowPaymentForm: shouldShowPaymentForm,
            });
            e.preventDefault();
            btn.classList.add('processing');
            btn.disabled = true;
            if (!document.getElementById('gwc-spinner-style')) {
                var style = document.createElement('style');
                style.id = 'gwc-spinner-style';
                style.innerHTML = '@keyframes gwc-spin { to { transform: rotate(360deg); } }';
                document.head.appendChild(style);
            }
            btn.innerHTML = '<span class="gwc-hf-spinner" style="display:inline-block; vertical-align:middle; margin-right:8px; width:18px; height:18px; border:2px solid currentColor; border-top-color:transparent; border-radius:50%; animation:gwc-spin 0.6s linear infinite;"></span> Processing...';
            window.location.href = appendCheckoutAttempt(instantUrl, checkoutAttemptId, 'provider_redirect');
            return;
        }

        if (btn.getAttribute('data-instant-charge') === '1' && !shouldShowPaymentForm) return;
        e.preventDefault();

        var selectedProductId = selectedProductIdForButton(btn);
        var productId = selectedProductId
            || btn.getAttribute('data-product-id')
            || getUrlParam(btn.getAttribute('href'), 'add-to-cart')
            || getUrlParam(btn.getAttribute('href'), 'gwc_product_id');
        if (!productId) {
            debugPaymentForm('checkout_click:blocked_missing_product_id', {
                button: getContainerDebugSummary(btn),
                href: btn.getAttribute('href') || '',
            });
            resetProcessingButtons();
            return;
        }

        // Product collections can be injected after the theme bundle binds its
        // direct handlers. Reconcile the CTA with the currently selected card at
        // click time so a visually selected plan can never open another plan.
        btn.setAttribute('data-product-id', productId);
        if (selectedProductId && btn.getAttribute('data-purchase-intent-required') !== '1') {
            var selectedHref = checkoutUrlForProduct(btn.getAttribute('href') || '', productId);
            var selectedFallback = checkoutUrlForProduct(btn.getAttribute('data-fallback') || '', productId);
            if (selectedHref) btn.setAttribute('href', selectedHref);
            if (selectedFallback) btn.setAttribute('data-fallback', selectedFallback);
        }

        var methods = btn.getAttribute('data-methods') || ((isPaypalAddToCart || isOrderPay) ? 'applepay,googlepay,paypal' : 'applepay,googlepay');
        var returnUrl = btn.getAttribute('data-return-url') || '';
        var fallbackUrl = btn.getAttribute('data-fallback') || btn.getAttribute('href') || '';
        var buttonLabel = (btn.textContent || '').trim();
        var primaryMethod = initialPaymentMethod;
        var provider = (btn.getAttribute('data-provider') || ((isPaypalAddToCart || isOrderPay) ? 'paypal' : '') || normalizeProviderFromPaymentMethod(primaryMethod) || 'paypal').toLowerCase();
        var paymentFormAction = btn.getAttribute('data-payment-form-action') || getDefaultPaymentFormAction(provider);
        var paymentFormNonce = btn.getAttribute('data-payment-form-nonce') || '';

        debugPaymentForm('checkout_click:provider_resolved', {
            productId: productId,
            methods: methods,
            returnUrl: returnUrl,
            fallbackUrl: fallbackUrl,
            buttonLabel: buttonLabel,
            paymentFormMode: paymentFormMode,
            expressShowForm: expressShowForm,
            shouldShowPaymentForm: shouldShowPaymentForm,
            primaryMethod: primaryMethod,
            provider: provider,
            paymentFormAction: paymentFormAction,
            hasPaymentFormNonce: !!paymentFormNonce,
        });

        if (!shouldShowPaymentForm) {
            trackCommerceMilestone('growtype_analytics_growtype_wc_begin_payment', primaryMethod, checkoutAttemptId);
            checkoutEvent('provider_redirect', primaryMethod, checkoutAttemptId);
            debugPaymentForm('checkout_click:redirecting_payment_form_disabled', {
                provider: provider,
                primaryMethod: primaryMethod,
                fallbackUrl: fallbackUrl,
            });

            btn.classList.add('processing');
            btn.setAttribute('aria-disabled', 'true');
            btn.disabled = true;

            if (fallbackUrl) {
                window.location.href = appendCheckoutAttempt(fallbackUrl, checkoutAttemptId, 'provider_redirect');
            } else {
                resetProcessingButtons();
            }

            return;
        }
        
        var modalBody = btn.closest('.modal-body');

        // Detect if we are inside a non-payment modal (like Starter Pack modal)
        var existingModal = btn.closest('.modal');
        if (existingModal && existingModal.id !== 'gwcPaymentFormModal') {
            debugPaymentForm('checkout_click:inside_non_payment_modal', {
                modal: getContainerDebugSummary(existingModal),
            });
            modalBody = null; // force new modal flow

            if (window.bootstrap && window.bootstrap.Modal) {
                var inst = window.bootstrap.Modal.getInstance(existingModal);
                if (inst) inst.hide();
            } else if (window.jQuery && window.jQuery.fn.modal) {
                window.jQuery(existingModal).modal('hide');
            } else {
                existingModal.classList.remove('show');
                existingModal.style.display = 'none';
                var backdrop = document.querySelector('.modal-backdrop');
                if (backdrop) backdrop.remove();
            }
        }

        if (!modalBody) {
            debugPaymentForm('checkout_click:using_new_payment_modal_flow');
            getOrCreatePaymentFormModal().then(function (modal) {
                var body = modal.querySelector('.modal-body-payment-form');
                if (!body) {
                    checkoutEvent('payment_form_error', primaryMethod, checkoutAttemptId, { error_code: 'missing_modal_body' });
                    debugPaymentForm('checkout_click:blocked_missing_modal_body_payment_form', {
                        modal: getContainerDebugSummary(modal),
                    });
                    return;
                }
                body.innerHTML = '';
                continueFlow(body, true, modal, existingModal);
            }).catch(function (err) {
                checkoutEvent('payment_form_error', primaryMethod, checkoutAttemptId, { error_code: 'modal_error' });
                debugPaymentForm('checkout_click:modal_flow_error', err);
                console.error('[wc-payment-form] Could not get modal:', err);
                resetProcessingButtons();
            });
            return;
        }

        debugPaymentForm('checkout_click:using_existing_modal_body_flow', {
            modalBody: getContainerDebugSummary(modalBody),
        });
        continueFlow(modalBody, false, null, existingModal);

        function continueFlow(modalBody, isNewModal, modal, fromModal) {
            debugPaymentForm('continue_flow:start', {
                isNewModal: isNewModal,
                modalBody: getContainerDebugSummary(modalBody),
                modal: getContainerDebugSummary(modal),
                fromModal: getContainerDebugSummary(fromModal),
            });

            var loader;
            var modalEl = modal || document.getElementById('gwcPaymentFormModal');
            if (modalEl) {
                var modalTitle = modalEl.querySelector('.modal-title');
                if (modalTitle) {
                    modalTitle.innerHTML = 'Pay with Card';
                }
            }

            function renderPaymentFormFailure(message) {
                hideSpinner(loader);
                modalBody.innerHTML = '';

                var panel = document.createElement('div');
                panel.className = 'gwc-payment-form-error';
                panel.setAttribute('role', 'alert');
                panel.style.cssText = 'display:flex; flex-direction:column; gap:12px; padding:8px 0;';

                var heading = document.createElement('strong');
                heading.textContent = 'Checkout could not load';
                panel.appendChild(heading);

                var copy = document.createElement('p');
                copy.textContent = message;
                copy.style.margin = '0';
                panel.appendChild(copy);

                var actions = document.createElement('div');
                actions.style.cssText = 'display:flex; flex-wrap:wrap; gap:8px;';

                var retryButton = document.createElement('button');
                retryButton.type = 'button';
                retryButton.className = 'btn btn-primary gwc-payment-form-retry';
                retryButton.textContent = 'Retry payment form';
                retryButton.addEventListener('click', function () {
                    modalBody.innerHTML = '';
                    loader = showSpinner(modalBody);
                    fetchForm();
                });
                actions.appendChild(retryButton);

                if (fallbackUrl) {
                    var fallbackLink = document.createElement('a');
                    fallbackLink.className = 'btn btn-outline-secondary gwc-payment-form-fallback-link';
                    fallbackLink.href = appendCheckoutAttempt(fallbackUrl, checkoutAttemptId, 'provider_redirect');
                    fallbackLink.textContent = 'Continue to secure checkout';
                    actions.appendChild(fallbackLink);
                }

                panel.appendChild(actions);
                modalBody.appendChild(panel);
            }

            function fetchForm() {
                var ajaxUrl = (window.growtype_wc_ajax && window.growtype_wc_ajax.url)
                    ? window.growtype_wc_ajax.url
                    : '/wp-admin/admin-ajax.php';

                if (!paymentFormAction) {
                    checkoutEvent('payment_form_error', primaryMethod, checkoutAttemptId, { error_code: 'missing_form_action' });
                    debugPaymentForm('fetch_form:blocked_missing_payment_form_action', {
                        provider: provider,
                        primaryMethod: primaryMethod,
                        fallbackUrl: fallbackUrl,
                    });
                    hideSpinner(loader);

                    if (fallbackUrl) {
                        checkoutEvent('provider_redirect', primaryMethod, checkoutAttemptId);
                        window.location.href = appendCheckoutAttempt(fallbackUrl, checkoutAttemptId, 'provider_redirect');
                    } else {
                        resetProcessingButtons();
                    }

                    return;
                }

                trackCommerceMilestone('growtype_analytics_growtype_wc_begin_payment', primaryMethod, checkoutAttemptId);

                debugPaymentForm('fetch_form:start', {
                    provider: provider,
                    action: paymentFormAction,
                    ajaxUrl: ajaxUrl,
                    hasNonce: !!getPaymentFormNonce(provider, paymentFormNonce),
                    productId: productId,
                    methods: methods,
                });

                resolveConfig().then(function () {
                    debugPaymentForm('fetch_form:config_ready_before_request', {
                        provider: provider,
                        hasAjaxConfig: !!window.growtype_wc_ajax,
                    });

                    return requestPaymentFormWithNonceRefresh({
                        send: function () {
                            var body = new URLSearchParams({
                                action: paymentFormAction,
                                nonce: getPaymentFormNonce(provider, paymentFormNonce),
                                product_id: productId,
                                methods: methods,
                                provider: provider,
                                payment_method: primaryMethod,
                                checkout_attempt_id: checkoutAttemptId,
                            });

                            return fetch(ajaxUrl, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                                credentials: 'same-origin',
                                cache: 'no-store',
                                body: body.toString(),
                            });
                        },
                        refreshNonce: function () {
                            return refreshPaymentFormNonce(ajaxUrl, provider);
                        },
                        applyNonce: function (nonce) {
                            paymentFormNonce = nonce;
                            btn.setAttribute('data-payment-form-nonce', nonce);
                        },
                    });
                })
                    .then(function (r) { return r.json(); })
                    .then(function (res) {
                        debugPaymentForm('fetch_form:response', {
                            provider: provider,
                            success: !!(res && res.success),
                            dataKeys: res && res.data ? Object.keys(res.data) : [],
                            containerId: res && res.data ? res.data.container_id : '',
                            productId: res && res.data ? res.data.product_id : '',
                            methods: res && res.data ? res.data.methods : '',
                            htmlLength: res && res.data && res.data.html ? res.data.html.length : 0,
                        });
                        hideSpinner(loader);

                        if (!res.success) {
                            checkoutEvent('payment_form_error', primaryMethod, checkoutAttemptId, { error_code: 'form_response_error' });
                            debugPaymentForm('fetch_form:blocked_ajax_error', res);
                            console.error('[wc-payment-form] AJAX error:', res);
                            resetProcessingButtons();
                            renderPaymentFormFailure('Your checkout session expired. Retry the payment form or continue to secure checkout.');
                            return;
                        }

                        if (res.data && res.data.total_price && modalEl) {
                            var modalTitle = modalEl.querySelector('.modal-title');
                            if (modalTitle) {
                                modalTitle.innerHTML = 'Total: ' + res.data.total_price;
                            }
                        }

                        var wrap = document.createElement('div');
                        wrap.className = 'gwc-checkout-payment-form';
                        wrap.style.cssText = 'opacity:0; transition:opacity 0.25s;';
                        wrap.innerHTML = res.data.html; // Safe: no <script> tags from server
                        modalBody.appendChild(wrap);
                        checkoutEvent('payment_form_loaded', primaryMethod, checkoutAttemptId);
                        debugPaymentForm('fetch_form:html_appended', {
                            wrap: getContainerDebugSummary(wrap),
                            modalBody: getContainerDebugSummary(modalBody),
                            mountContainer: getContainerDebugSummary(document.getElementById(res.data.container_id)),
                        });

                        // Fade in
                        requestAnimationFrame(function () {
                            requestAnimationFrame(function () {
                                wrap.style.opacity = '1';
                            });
                        });

                        if (res.data.mount_express === false) {
                            debugPaymentForm('fetch_form:subscription_redirect_only', {
                                productId: res.data.product_id,
                            });
                            return;
                        }

                        // Boot provider-specific express buttons
                        debugPaymentForm('fetch_form:dispatch_mount_express', {
                            provider: provider,
                            container: '#' + res.data.container_id,
                            productId: parseInt(res.data.product_id, 10),
                            methods: res.data.methods,
                            returnUrl: returnUrl,
                            fallback: fallbackUrl,
                            label: buttonLabel,
                            showForm: shouldShowPaymentForm,
                            paymentMethod: primaryMethod,
                            paymentFormAction: paymentFormAction,
                            mountContainer: getContainerDebugSummary(document.getElementById(res.data.container_id)),
                        });
                        document.dispatchEvent(new CustomEvent('growtype_wc_payment_request', {
                            detail: {
                                provider: provider,
                                type: 'mount_express',
                                container: '#' + res.data.container_id,
                                productId: parseInt(res.data.product_id, 10),
                                method: res.data.methods,
                                returnUrl: returnUrl,
                                fallback: fallbackUrl,
                                label: buttonLabel,
                                showForm: shouldShowPaymentForm,
                                paymentMethod: primaryMethod,
                                paymentFormAction: paymentFormAction,
                                checkoutAttemptId: checkoutAttemptId,
                            }
                        }));
                    })
                    .catch(function (err) {
                        checkoutEvent('payment_form_error', primaryMethod, checkoutAttemptId, { error_code: 'form_fetch_error' });
                        debugPaymentForm('fetch_form:error', err);
                        hideSpinner(loader);
                        console.error('[wc-payment-form] Fetch failed:', err);
                        resetProcessingButtons();
                        renderPaymentFormFailure('We could not load the secure payment form. Retry or continue to secure checkout.');
                    });
            }

            if (!isNewModal) {
                debugPaymentForm('continue_flow:existing_modal_fade_out_start', {
                    modalBody: getContainerDebugSummary(modalBody),
                });
                // Fade out existing modal body content
                var children = Array.from(modalBody.children);
                children.forEach(function (el) {
                    if (el.classList.contains('btn-close')) return;
                    el.style.transition = 'opacity 0.2s';
                    el.style.opacity = '0';
                    el.style.pointerEvents = 'none';
                });

                setTimeout(function () {
                    debugPaymentForm('continue_flow:existing_modal_fetch_after_fade');
                    children.forEach(function (el) {
                        if (el.classList.contains('btn-close')) return;
                        el.style.display = 'none';
                    });

                    loader = showSpinner(modalBody);
                    fetchForm();
                }, 220);
            } else {
                debugPaymentForm('continue_flow:new_modal_show_then_fetch', {
                    openDelay: fromModal ? 150 : 0,
                });
                // New modal flow: show spinner, open modal, fetch form
                loader = showSpinner(modalBody);
                var openDelay = fromModal ? 150 : 0;
                setTimeout(function () {
                    showPaymentFormModal(modal);
                    fetchForm();
                }, openDelay);
            }
        }
    }, true);

    // ── Boot on page load ─────────────────────────────────────────────────────

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            debugPaymentForm('dom_ready:DOMContentLoaded');
            initMounts(document);
            openModalOnInstantChargeFail();
        });
    } else {
        debugPaymentForm('dom_ready:already_ready');
        initMounts(document);
        openModalOnInstantChargeFail();
    }

    trackCheckoutOfferImpressions(document);
    window.setTimeout(function () { trackCheckoutOfferImpressions(document); }, 250);
    var returnParams = new URLSearchParams(window.location.search);
    var returnStage = returnParams.get('gwc_checkout_stage');
    if (returnStage === 'provider_return' || returnStage === 'provider_cancelled') {
        checkoutEvent(returnStage, '', returnParams.get('gwc_checkout_attempt'));
    }
    window.growtypeWcCheckoutTracking = {
        track: checkoutEvent,
        appendAttempt: appendCheckoutAttempt,
    };

    /**
     * If the page was loaded after a failed instant charge (upsell_failed=1),
     * automatically open the payment modal so the user can pay with another method.
     * The instant-charge-url is cleared from the button so the modal path is taken.
     */
    function openModalOnInstantChargeFail() {
        var params = new URLSearchParams(window.location.search);
        if (!params.has('upsell_failed')) {
            debugPaymentForm('instant_charge_fail:no_url_flag');
            return;
        }

        var btn = document.querySelector('.' + BTN_CLASS + '[data-product-id]');
        if (!btn) {
            debugPaymentForm('instant_charge_fail:blocked_missing_checkout_button');
            return;
        }
        debugPaymentForm('instant_charge_fail:opening_modal', {
            button: getContainerDebugSummary(btn),
        });

        // Remove the instant-charge attributes so the normal modal flow is used
        btn.removeAttribute('data-instant-charge');
        btn.removeAttribute('data-instant-charge-url');

        // Small delay to let the page and any error alerts render first
        setTimeout(function () {
            btn.click();
        }, 600);
    }

    // ── MutationObserver — catch dynamically injected mounts ──────────────────

    if (window.MutationObserver) {
        new MutationObserver(function (mutations) {
            mutations.forEach(function (m) {
                m.addedNodes.forEach(function (node) {
                    if (node.nodeType !== 1) return; // elements only
                    if (node.classList && node.classList.contains(MOUNT_CLASS)) {
                        debugPaymentForm('mutation_observer:mount_node_added', {
                            node: getContainerDebugSummary(node),
                        });
                        initMounts(node.parentElement || document);
                    } else if (node.querySelectorAll) {
                        var found = node.querySelectorAll('.' + MOUNT_CLASS).length;
                        if (found) {
                            debugPaymentForm('mutation_observer:mount_descendant_added', {
                                found: found,
                                node: getContainerDebugSummary(node),
                            });
                        }
                        initMounts(node);
                        trackCheckoutOfferImpressions(node);
                    }
                });
            });
        }).observe(document.body, { childList: true, subtree: true });
    }

    // ── Public API ────────────────────────────────────────────────────────────

    window.gwcPaymentForm = {
        /** Manually boot mount points inside a given element (or document). */
        init: initMounts,
        showSpinner: showSpinner,
        hideSpinner: hideSpinner,
        setDebug: setPaymentFormDebug,
        isDebugEnabled: isPaymentFormDebugEnabled,
    };

})();
